/**
 * Compute the completion status of each wizard step based on formData.
 *
 * Returns an array (one entry per step) of:
 *   'complete' — all required fields filled (or no required fields and at least one field has data)
 *   'partial'  — some data entered but required fields are missing
 *   'empty'    — no data entered at all
 */

/** True when a single value has meaningful data. */
function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'boolean') return false; // booleans (auto-checkboxes) don't count
  return false;
}

/** True when a flat section object has at least one non-null currency field. */
function sectionHasData(data, excludeSuffixes = ['_auto']) {
  if (!data || typeof data !== 'object') return false;
  return Object.entries(data).some(([key, val]) => {
    if (excludeSuffixes.some((s) => key.endsWith(s))) return false;
    return hasValue(val);
  });
}

/**
 * Checks for nested auto objects (auto_1, auto_2, auto_3 in auto_transport).
 */
function autoTransportHasData(data) {
  if (!data) return false;
  // Check nested auto objects
  for (const k of ['auto_1', 'auto_2', 'auto_3']) {
    if (data[k] && hasValue(data[k].amount)) return true;
  }
  // Check flat fields
  for (const [key, val] of Object.entries(data)) {
    if (['auto_1', 'auto_2', 'auto_3'].includes(key)) continue;
    if (hasValue(val)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Per-step status
// ---------------------------------------------------------------------------

function incomeStatus(data) {
  if (data?._skipped) return 'complete';
  if (!data || !data.sources) return 'empty';
  const anySrc = data.sources.some((s) => hasValue(s.name) || hasValue(s.amount));
  if (!anySrc) return 'empty';
  const s0 = data.sources[0];
  if (hasValue(s0.name) && hasValue(s0.amount)) return 'complete';
  return 'partial';
}

function flatStatus(data) {
  if (data?._skipped) return 'complete';
  if (!sectionHasData(data)) return 'empty';
  return 'complete'; // no required fields — any data = complete
}

function autoTransportStatus(data) {
  if (data?._skipped) return 'complete';
  if (!autoTransportHasData(data)) return 'empty';
  return 'complete';
}

// ---------------------------------------------------------------------------
// Map step keys to their status evaluators
// ---------------------------------------------------------------------------

const STATUS_FNS = {
  welcome:              ()      => 'complete', // always done once visited
  import:               (data)  => (data?.files?.length ? 'complete' : 'empty'),
  income:               incomeStatus,
  housing:              flatStatus,
  bills_utilities:      flatStatus,
  auto_transport:       autoTransportStatus,
  medical:              flatStatus,
  insurance:            flatStatus,
  entertainment_travel: flatStatus,
  personal_family_care: flatStatus,
  charity:              flatStatus,
  legal_professional:   flatStatus,
  kids_education:       flatStatus,
  savings:              flatStatus,
  pets:                 flatStatus,
  debts:                flatStatus,
  review:               ()      => 'empty', // review has no own data
};

/**
 * Given the full formData and the STEPS array, return an array of status
 * strings: 'complete' | 'partial' | 'empty'.
 */
export function computeStepStatuses(formData, steps, currentStep) {
  return steps.map((step, idx) => {
    // Welcome: mark complete once user has moved past it
    if (step.key === 'welcome') {
      return currentStep > 0 ? 'complete' : 'empty';
    }
    const fn = STATUS_FNS[step.key] || flatStatus;
    return fn(formData[step.key]);
  });
}
